import axios from 'axios';
import * as Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import React from 'react';
import { FGBLocalStorage } from '../../../utilities/helperFunctions';
import Pixel, {
    getTrackingPropsCookie,
} from '../../Analytics/VisibilityPixel/VisibilityPixel';
import { buildImagePath } from '../../JobComponents/Company/Image/logo';
import CreateAccountLayout from './Layouts/CreateAccountLayout/CreateAccountLayout';
import FBReauthorizationConfirmationLayout from './Layouts/FBReauthorizationConfirmationLayout/FBReauthorizationConfirmationLayout';
import FBReauthorizationLayout from './Layouts/FBReauthorizationLayout/FBReauthorizationLayout';
import LogInLayout from './Layouts/LogInLayout/LogInLayout';
import ResetPasswordConfirmationLayout from './Layouts/ResetPasswordConfirmationLayout/ResetPasswordConfirmationLayout';
import ResetPasswordLayout from './Layouts/ResetPasswordLayout/ResetPasswordLayout';
import './LoginLayoutComponent.scss';

export const POPUP_MODE = 'popup';
export const EMBED_MODE = 'embed';
export const CREATE_LAYOUT = 'create';
export const LOGIN_LAYOUT = 'login';
export const RESET_PASSWORD_CONFIRMATION_LAYOUT = 'password-confirmation';
export const RESET_PASSWORD_LAYOUT = 'password';
export const FACEBOOK_REAUTHORIZATION_LAYOUT = 'facebook-reauthorization';
export const FACEBOOK_REAUTHORIZATION_CONFIRMATION_LAYOUT =
    'facebook-reauthorization-confirmation';

const LOGIN_TRIGGER_COOKIE = 'LOGIN-TRIGGER';

export default class LoginLayoutComponent extends React.PureComponent {
    constructor(props) {
        super();
        this.state = {
            displayMode: props.displayMode,
            layoutType: props.layoutType,
            hidePopup: false,
            email: null,
            password: null,
            username: null,
            name: null,
            usernameError: null,
            nameError: null,
            emailError: null,
            passwordError: null,
            generalError: null,
            loaded: true,
        };
    }

    static propTypes = {
        /**
         * Whether or not the component will be displayed in
         */
        displayMode: PropTypes.oneOf([POPUP_MODE, EMBED_MODE]).isRequired,
        /**
         * The secondary subtitle text
         */
        callToActionSubTitle: PropTypes.string,
        /**
         * Whether or not the component will be displayed in
         */
        layoutType: PropTypes.oneOf([
            CREATE_LAYOUT,
            LOGIN_LAYOUT,
            RESET_PASSWORD_LAYOUT,
            RESET_PASSWORD_CONFIRMATION_LAYOUT,
            FACEBOOK_REAUTHORIZATION_LAYOUT,
            FACEBOOK_REAUTHORIZATION_CONFIRMATION_LAYOUT,
        ]).isRequired,
        /**
         * callback function to close modal, is required if in POPUP_MODE
         */
        hidePopup: PropTypes.func,
        /**
         * auth0 config
         */
        auth0: PropTypes.shape({
            domain: PropTypes.string,
            clientId: PropTypes.string,
            db: PropTypes.string,
        }).isRequired,
        /**
         * sign up success redirect uri
         */
        loginRedirectPath: PropTypes.string.isRequired,
        /**
         * redirectURI
         */
        redirectUri: PropTypes.string.isRequired,
        /**
         * Object containing any additional analytics metrics we need to fire with the auth open pixel
         */
        loginMetrics: PropTypes.object,
        /**
         * if user filled out a survey, she will have an temp survey id
         */
        incompleteRegistrationId: PropTypes.string,
        /**
         * if user is invited by other user, she will have and invitation token
         */
        invitationToken: PropTypes.string,
        /**
         *  true: show the close-button, false: do not show the close-button
         */
        allowClosing: PropTypes.bool,
        inviteHash: PropTypes.string,
        groupInviteHash: PropTypes.string,
        connectionInviteHash: PropTypes.string,
    };

    static defaultProps = {
        loginMetrics: {
            login_trigger: 'generic',
        },
        groupInviteHash: null,
        employerInviteHash: null,
        connectionInviteHash: null,
    };

    componentDidMount() {
        if (this.props.loginMetrics) {
            Cookies.set(
                LOGIN_TRIGGER_COOKIE,
                this.props.loginMetrics.login_trigger,
                { expires: 1095 }
            );
            FGBLocalStorage.setItem(
                'last_login_metrics',
                this.props.loginMetrics
            );
        }
    }

    changeLayoutState = (layoutType) => {
        this.setState({
            layoutType,
            password: null,
            nameError: null,
            emailError: null,
            usernameError: null,
            passwordError: null,
            generalError: null,
        });
    };

    handleEmailInput = (email) => {
        const trimmedEmail = email.replace(/ /g, '');
        this.setState({
            email: trimmedEmail,
            emailError: null,
            nameError: null,
            usernameError: null,
            passwordError: null,
            generalError: null,
        });
    };

    handlePasswordInput = (password) => {
        this.setState({
            password,
            emailError: null,
            nameError: null,
            usernameError: null,
            passwordError: null,
            generalError: null,
        });
    };

    handleUsernameInput = (username) => {
        this.setState({
            username: username.trim(),
            nameError: null,
            usernameError: null,
            emailError: null,
            passwordError: null,
            generalError: null,
        });
    };

    handleNameInput = (name) => {
        this.setState({
            name: name.trim(),
            nameError: null,
            usernameError: null,
            emailError: null,
            passwordError: null,
            generalError: null,
        });
    };

    resetErrorState = () => {
        this.setState({
            usernameError: null,
            emailError: null,
            nameError: null,
            passwordError: null,
            generalError: null,
        });
    };

    setupRedirectUri = (successUri) => {
        const uriEncodedSuccessUri = encodeURIComponent(successUri);
        let redirectUri =
            this.props.redirectUri + '?redirect=' + uriEncodedSuccessUri;
        let char = uriEncodedSuccessUri.indexOf('?') >= 0 ? '&' : '?';

        //TODO: delete after launch new survey
        if (this.props.incompleteRegistrationId) {
            redirectUri += `${char}incompleteRegistrationId=${this.props.incompleteRegistrationId}`;
        }

        const TrackingPropsCookie = getTrackingPropsCookie();
        const loginMetrics = TrackingPropsCookie
            ? Object.assign(TrackingPropsCookie, this.props.loginMetrics)
            : this.props.loginMetrics;
        redirectUri += `&loginMetrics=${encodeURI(
            JSON.stringify(loginMetrics)
        )}`;

        return redirectUri;
    };

    handleAuth0Error = (err, operation) => {
        let error = {};
        switch (err.code) {
            case `error in email - email format validation failed: ${this.state.email}`:
                error = {
                    message: 'Invalid email address.',
                    type: 'user',
                };
                break;
            case 'invalid_password':
                error = {
                    message: 'Password must be at least 8 characters.',
                    type: 'password',
                };
                this.setState({ showPasswordHint: true });
                break;
            case 'invalid email address':
                error = {
                    message: 'Invalid email address.',
                    type: 'user',
                };
                break;
            case 'user_exists':
                error = {
                    message: 'The user already exists.',
                    type: 'user',
                };
                break;
            case 'access_denied':
                error = {
                    message: 'Invalid user credentials.',
                    type: 'general',
                };
                break;
            case 'blocked_user':
                error = {
                    message: 'User is blocked.',
                    type: 'general',
                };
                break;
            case 'password_leaked':
                error = {
                    message:
                        "This login attempt has been blocked because the password you're using was previously disclosed through a data breach (not in this application).",
                    type: 'general',
                };
                break;
            case 'rate_limited':
                error = {
                    message:
                        'You have made to many login requests please wait a few minutes to try again.',
                    type: 'general',
                };
                break;
            case 'too_many_attempts':
                error = {
                    message:
                        'Your account has been blocked after multiple consecutive login attempts. We’ve sent you an email with instructions on how to unblock it.',
                    type: 'general',
                };
                break;
            case 'username_exists':
                error = {
                    message: 'Username already exists.',
                    type: 'username',
                };
                break;
            case 'invalid_username':
                error = {
                    message: 'Username must be at most 32 characters.',
                    type: 'username',
                };
                break;
            case 'invalid_name':
                error = {
                    message: 'Your name is limited to 100 characters.',
                    type: 'name',
                };
                break;
            default:
                error = operation + ' failed';
        }

        console.log(error);
        this.setAuth0ErrorMsg(error);
    };

    setAuth0ErrorMsg = (auth0ErrorMSg) => {
        let errorType = 'generalError';
        switch (auth0ErrorMSg.type) {
            case 'general':
                errorType = 'generalError';
                break;
            case 'user':
                errorType = 'emailError';
                break;
            case 'password':
                errorType = 'passwordError';
                break;
            case 'username':
                errorType = 'usernameError';
                break;
            case 'name':
                errorType = 'nameError';
                break;
            default:
                errorType = 'generalError';
        }

        this.setState({
            [errorType]: auth0ErrorMSg.message,
        });
    };

    serializeMetaData = () =>
        JSON.stringify({
            inviteHash: this.props.inviteHash,
            connectionInviteHash: this.props.connectionInviteHash,
            groupInviteHash: this.props.groupInviteHash,
            employerInviteHash: this.props.employerInviteHash,
        });

    setLoginMeta = async (redirectPath, serializedMetaData) => {
        let url = `/auth-v2/init-reg?rp=${encodeURIComponent(
            redirectPath
        )}&md=${encodeURIComponent(serializedMetaData)}`;
        try {
            const result = await axios.post(url);
            if (
                result.data &&
                result.data.success === true &&
                result.data.cookieId
            ) {
                Cookies.set('IAK', result.data.cookieId, { expires: 1095 });
                Cookies.set('IAK-DEBUG', result.data.cookieId, {
                    expires: 1095,
                });
            }
        } catch (e) {}
    };

    onLoginInternal = async (option = 'local', customRedirect = null) => {
        this.setState({ loaded: false });
        const redirectPath = customRedirect || this.props.loginRedirectPath;

        let serializedMetaData = this.serializeMetaData();

        switch (option) {
            case 'google':
            case 'facebook':
                await this.setLoginMeta(redirectPath, serializedMetaData);
                window.location = `/auth-v2/init-social/${option}?rp=${encodeURIComponent(
                    redirectPath
                )}&md=${encodeURIComponent(serializedMetaData)}`;
                return;
                break;
            default:
                try {
                    const result = await axios.post(
                        `/auth-v2/user-pass/login`,
                        {
                            email: this.state.email,
                            password: this.state.password,
                        }
                    );

                    let data = result.data;
                    this.setLoadedStateForLoaderComponent(true);
                    if (data.success === true) {
                        this.redirectToPath(redirectPath);
                    } else {
                        this.handleAuth0Error({ code: data.err }, 'Login');
                    }
                } catch (err) {
                    console.error(err);
                }
        }
    };

    onInternalSignup = (option = 'local', customRedirect = null) => {
        if (option !== 'local') {
            this.onLoginInternal(option, customRedirect);
            return;
        }
        this.redirectToPath(customRedirect);
    };

    redirectToPath = (redirectPath) => {
        const origin = window.location.origin;
        const newState = {
            origin,
            pathName: redirectPath,
        };

        window.history.pushState(newState, '', `${origin}${redirectPath}`);
        setTimeout(() => {
            window.location.reload();
        }, 100);
    };

    handleInternalAuthError = (error, type, operation) => {
        switch (error) {
            case 'email is not valid':
                this.setAuth0ErrorMsg({
                    type,
                    message:
                        'The email in invalid. Please enter a valid email.',
                });
                break;
            case 'email does not exist':
                this.setAuth0ErrorMsg({
                    type,
                    message: 'The user does not exist.',
                });
                break;
            case 'user has been deactivated':
                this.setAuth0ErrorMsg({
                    type,
                    message: 'User has been deactivated.',
                });
                break;
            case 'server failed processing request':
                this.setAuth0ErrorMsg({
                    type,
                    message: 'Something went wrong. Please try again.',
                });
                break;
            default:
                this.setAuth0ErrorMsg({
                    type,
                    message: `${operation} failed.`,
                });
                break;
        }
    };

    internalPasswordReset = async () => {
        this.setState({ loaded: false });

        const payload = { email: this.state.email };

        try {
            await axios.post('/api/auth-v2/forgot-password', payload);
            this.setState({ loaded: true });

            this.changeLayoutState(RESET_PASSWORD_CONFIRMATION_LAYOUT);
            this.resetErrorState();
        } catch (error) {
            this.setState({ loaded: true });

            const { data } = error.response || {};
            const { email: emailErrors, _general: generalError } =
                data.errors || {};
            const emailError = (emailErrors && emailErrors[0]) || generalError;

            this.handleInternalAuthError(emailError, 'user', 'Password Reset');
        }
    };

    onResendFBPermissionInternal = (successUri) => {
        window.location = `/auth-v2/init-social/facebook-reauth?rp=${encodeURIComponent(
            successUri
        )}&md=${encodeURIComponent(this.serializeMetaData())}`;
    };

    setLoadedStateForLoaderComponent = (value) => {
        this.setState({ loaded: value });
    };

    renderComponents = () => {
        let titleText = null;
        let optionChangeComponent = null;
        let Component = null;

        switch (this.state.layoutType) {
            case CREATE_LAYOUT:
                titleText = 'Create an account';
                optionChangeComponent = (
                    <div className="switch-layout-component">
                        Already a member?{' '}
                        <a onClick={() => this.changeLayoutState(LOGIN_LAYOUT)}>
                            Log in >
                        </a>
                    </div>
                );
                Component = (
                    <CreateAccountLayout
                        inviteHash={this.props.inviteHash}
                        groupInviteHash={this.props.groupInviteHash}
                        employerInviteHash={this.props.employerInviteHash}
                        connectionInviteHash={this.props.connectionInviteHash}
                        auth0={this.props.auth0}
                        signupRedirectPath={this.props.loginRedirectPath}
                        setAuth0ErrorMsg={this.resetErrorState}
                        handleAuth0Error={this.handleAuth0Error}
                        auth0ErrorMSg={this.state.auth0ErrorMSg}
                        emailError={this.state.emailError}
                        usernameError={this.state.usernameError}
                        nameError={this.state.nameError}
                        handleNameInput={this.handleNameInput}
                        passwordError={this.state.passwordError}
                        generalError={this.state.generalError}
                        handleEmailInput={this.handleEmailInput}
                        handleUsernameInput={this.handleUsernameInput}
                        handlePasswordInput={this.handlePasswordInput}
                        setErrorState={this.setErrorState}
                        email={this.state.email}
                        username={this.state.username}
                        name={this.state.name}
                        password={this.state.password}
                        onLogin={this.onInternalSignup}
                        loaded={this.state.loaded}
                        setLoadedStateForLoaderComponent={
                            this.setLoadedStateForLoaderComponent
                        }
                        callToActionSubTitle={this.props.callToActionSubTitle}
                        invitationToken={this.props.invitationToken}
                        loginMetrics={this.props.loginMetrics}
                        incompleteRegistrationId={
                            this.props.incompleteRegistrationId
                        }
                    />
                );
                break;
            case LOGIN_LAYOUT:
                titleText =
                    this.props.loginMetrics &&
                    this.props.loginMetrics.login_trigger === 'employer-login'
                        ? 'Employer Login'
                        : 'Log In';
                optionChangeComponent = (
                    <div className="switch-layout-component small-screen-fix">
                        Not a member yet?{' '}
                        <a
                            onClick={() =>
                                this.changeLayoutState(CREATE_LAYOUT)
                            }
                        >
                            Join us >
                        </a>
                    </div>
                );
                Component = (
                    <LogInLayout
                        resetPasswordCallBack={() =>
                            this.changeLayoutState(RESET_PASSWORD_LAYOUT)
                        }
                        onLogin={this.onLoginInternal}
                        emailError={this.state.emailError}
                        passwordError={this.state.passwordError}
                        generalError={this.state.generalError}
                        handleEmailInput={this.handleEmailInput}
                        handlePasswordInput={this.handlePasswordInput}
                        email={this.state.email}
                        password={this.state.password}
                        loaded={this.state.loaded}
                        callToActionSubTitle={this.props.callToActionSubTitle}
                    />
                );
                break;
            case RESET_PASSWORD_LAYOUT:
                titleText = 'Reset your password';
                optionChangeComponent = (
                    <div className="switch-layout-component bottom-spacing">
                        Back to{' '}
                        <a onClick={() => this.changeLayoutState(LOGIN_LAYOUT)}>
                            Login >
                        </a>
                    </div>
                );
                Component = (
                    <ResetPasswordLayout
                        onClick={this.internalPasswordReset}
                        handleEmailInput={this.handleEmailInput}
                        email={this.state.email}
                        emailError={this.state.emailError}
                        loaded={this.state.loaded}
                        callToActionSubTitle={this.props.callToActionSubTitle}
                    />
                );
                break;
            case RESET_PASSWORD_CONFIRMATION_LAYOUT:
                titleText = 'Reset your password';
                optionChangeComponent = null;
                Component = (
                    <ResetPasswordConfirmationLayout
                        onClick={() => this.changeLayoutState(LOGIN_LAYOUT)}
                    />
                );
                break;
            case FACEBOOK_REAUTHORIZATION_LAYOUT:
                titleText = 'Just one thing...';
                optionChangeComponent = null;
                Component = (
                    <FBReauthorizationLayout
                        onClick={() =>
                            this.onResendFBPermissionInternal(
                                this.props.loginRedirectPath
                            )
                        }
                    />
                );
                break;
            case FACEBOOK_REAUTHORIZATION_CONFIRMATION_LAYOUT:
                titleText = 'Got it';
                optionChangeComponent = null;
                Component = (
                    <FBReauthorizationConfirmationLayout
                        onClick={() => this.changeLayoutState(LOGIN_LAYOUT)}
                    />
                );
                break;
            default:
                console.error(
                    'No title set for given type:' + this.props.layoutType
                );
        }
        const title = (
            <div className="login-layout-main-header">{titleText}</div>
        );
        return {
            title,
            Component,
            optionChangeComponent,
        };
    };

    showPopupHeaders = (allowClosing = true) => {
        const imageSources = [
            {
                src: '/v1528477802/icons/logo_new.png',
                size: 'headerLogo',
            },
            {
                src: '/v1/Close_X_sw0ud2.png',
                size: 'close',
                onClick: this.props.hidePopup,
            },
        ];
        const imageTransforms = {
            headerLogo: 'c_scale,f_auto,q_auto,h_35',
            close: 'c_scale,f_auto,q_auto,w_22,h_22',
        };
        return (
            <div className="popup-layout-header-components">
                {imageSources.map((image, idx) => {
                    const src = buildImagePath(
                        image.src,
                        image.size,
                        imageTransforms
                    );
                    if (image.size === 'close') {
                        return allowClosing ? (
                            <img
                                key={idx}
                                className={image.size}
                                src={src}
                                onClick={image.onClick}
                            />
                        ) : null;
                    } else {
                        return (
                            <img
                                key={idx}
                                className={image.size}
                                src={src}
                                onClick={image.onClick}
                            />
                        );
                    }
                })}
            </div>
        );
    };

    render() {
        const components = this.renderComponents();
        let pixelMetrics = Object.assign({}, this.props.loginMetrics, {
            login_opened: 1,
        });
        return (
            <div className="component-Login-LoginLayoutComponent">
                {this.props.displayMode === POPUP_MODE
                    ? this.showPopupHeaders(this.props.allowClosing)
                    : null}
                {components.title}
                {components.Component}
                {components.optionChangeComponent}
                {this.state.layoutType === CREATE_LAYOUT ? (
                    <div className="disclosure-text-component">
                        <div className="text">
                            By registering, I am agreeing to the
                            <a
                                className="ml-1 mr-1"
                                href="/terms-and-conditions"
                            >
                                Terms and Conditions
                            </a>
                            and{' '}
                            <a className="ml-1 mr-1" href="/privacy-policy">
                                Privacy Policy
                            </a>{' '}
                            of this site.
                        </div>
                    </div>
                ) : null}
                <Pixel metrics={pixelMetrics} />
            </div>
        );
    }
}
